import type { FC, FormEvent, MouseEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';

import styles from '@pp/core/src/components/game/autoPlay/AutoPlay.module.css';
import { Drawer } from '@pp/core/src/components/game/drawer/Drawer';
import { GameButton } from '@pp/core/src/components/game/gameButton/gameButton';
import { GameRadioGroup } from '@pp/core/src/components/game/gameRadioGroup/GameRadioGroup';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';

import { Bets } from '../../config/bets';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';

import autoPlayStyles from './AutoPlay.module.css';


const AUTOPLAY_ROUND_OPTIONS: [string, string, string, string, string] = ['5', '10', '25', '50', '100']; // Eventually this will come from the server
export interface AutoPlayViewProps {
  /** Flag indicating weither the autoplay is in progress or not */
  isInProgress: boolean;
  /** On clicking the stop button */
  onStop: (event: MouseEvent<HTMLButtonElement>) => void;
  /** On submitting the form (Start/Modify button click) */
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  /** Flag indicating whether the autoplay drawer is openned or not (visible) */
  open: boolean;
  /** Number of autoplay rounds left */
  roundsLeft: number;
  /** Should the start button be enabled */
  startEnabled: boolean;
  /** On clicking close button */
  toggleOpen: (event?: MouseEvent<HTMLElement>) => void;
  /** Total raw bet placed so far */
  totalBet: number;
  /** React element for displaying total bet (string for storybook)t */
  totalBetNode: ReactElement | string;
  /** Additional class name */
  className?: string;
  /** React element to display limits */
  limitNode?: ReactElement;
  betId: Bets;
}

export const AutoPlayView: FC<AutoPlayViewProps> = ({
  isInProgress,
  limitNode,
  onStop,
  onSubmit,
  open,
  roundsLeft,
  startEnabled,
  toggleOpen,
  totalBet,
  totalBetNode,
  betId,
}) => {
  const [selectedAmount, setSelectedAmount] = useState(AUTOPLAY_ROUND_OPTIONS[0]);
  const [inProgressState, setProgressState] = useState(false);

  useEffect(() => {
    if (open) {
      setProgressState(isInProgress);
    }
  }, [isInProgress, open]);

  // const isModifyDisabled = !(roundsLeft !== parseInt(selectedAmount, 10) && totalBet > 0);

  const currentBet = useBetSlice.bets();
  const earlybets = useHighflyerStateSlice.getEarlyBetAmount(betId);
  const isModifyDisabled =
    (currentBet ? (currentBet.betSpots[betId]?.value > 0 ? currentBet.betSpots[betId]?.value : earlybets) : 0) <= 0;

  return (
    <Drawer
      data-testid="autoplay-drawer"
      end={true}
      onClose={toggleOpen}
      open={open}
      title={<L10nText tk="AUTOPLAY" />}
    >
      <form className={styles.autoplay} onSubmit={onSubmit}>
        {limitNode ? (
          <div className={`${styles.autoplay__limits} ${autoPlayStyles.autoplay__limits}`}>{limitNode}</div>
        ) : null}
        <L10nText tk="AUTOPLAYS_NUMBER" />
        <GameRadioGroup
          className={styles.autoplay__options}
          name="amountValue"
          onChange={(val) => setSelectedAmount(val)}
          options={AUTOPLAY_ROUND_OPTIONS.map((value) => ({ label: <>{value}</>, value }))}
          value={selectedAmount}
        />
        <L10nText tk="BET_PER_ROUND" />{' '}
        <strong className={totalBet > 0 ? '' : styles.autoplay__bet_error}>{totalBetNode}</strong>
        <div className={styles.autoplay__controls}>
          {inProgressState ? (
            <>
              <GameButton
                data-testid="autoplay-stop-button"
                grow
                label={<L10nText tk="STOP" />}
                onClick={onStop}
                style={{ marginBottom: 8 }}
                type="button"
                forceOldStyle
              />
              <GameButton
                data-testid="autoplay-modify-button"
                disabled={isModifyDisabled}
                active
                grow
                label={<L10nText tk="MODIFY" />}
                type="submit"
                forceOldStyle
              />
            </>
          ) : (
            <GameButton
              data-testid="autoplay-start-button"
              disabled={!startEnabled}
              active
              grow
              label={<L10nText tk="AUTOPLAY_START" />}
              type="submit"
              forceOldStyle
            />
          )}
        </div>
      </form>
    </Drawer>
  );
};
